import { Component, createRef } from 'preact';

/**
 * Fixed-height row virtualizer. Same pattern the deck builder uses to
 * stay smooth at 1000+ tiles. Only the rows currently in (or near)
 * the viewport are mounted in the DOM. Scroll events are rAF-throttled
 * and only re-render when the visible window has actually shifted.
 *
 * Why this exists: the achievements panel + leaderboard + any other
 * full-list scroll on the hub share the same Chromium-quirk pattern.
 * With ~40+ heavy-styled rows in the DOM under a `backdrop-filter`
 * parent, scroll wheel events queue up faster than the browser can
 * paint them, so the user hears the looping scroll-sfx draining past
 * their gesture and sees rows "spawn in" 1-2 frames late. Cutting the
 * mounted-row count to ~10–20 makes per-frame paint trivial and the
 * scroll engine drains in lockstep with input.
 *
 * Usage:
 *   <VirtualList
 *     items={someArray}
 *     itemHeight={48}
 *     renderItem={(item, index) => <Row item={item} />}
 *     className="flex-1 px-4 py-3"
 *   />
 *
 * Constraints:
 *   - Every row must render at exactly `itemHeight` pixels tall.
 *     Mixed heights need a measuring/cached-heights variant.
 *   - Children other than the list (a header/footer/empty-state)
 *     are passed via `header` / `footer` props, NOT children, to keep
 *     the virtual offset math sane.
 */
export default class VirtualList extends Component {
  state = { scrollTop: 0, viewport: 0 };
  scrollRef = createRef();
  _raf = null;
  _resizeObs = null;

  componentDidMount() {
    const el = this.scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', this.handleScroll, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObs = new ResizeObserver(() => {
        const cur = this.scrollRef.current;
        if (cur) this.setState({ viewport: cur.clientHeight });
      });
      this._resizeObs.observe(el);
    }
    this.setState({ viewport: el.clientHeight });
  }

  componentWillUnmount() {
    const el = this.scrollRef.current;
    if (el) el.removeEventListener('scroll', this.handleScroll);
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._resizeObs) this._resizeObs.disconnect();
  }

  // rAF-throttled scroll handler. At most one setState per frame.
  // Without this, rapid wheel deltas (especially trackpad momentum)
  // would call setState dozens of times per frame, each forcing a
  // diff/render, which would defeat the whole purpose.
  handleScroll = () => {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      const el = this.scrollRef.current;
      if (!el) return;
      const scrollTop = el.scrollTop;
      if (scrollTop !== this.state.scrollTop) {
        this.setState({ scrollTop });
      }
    });
  };

  scrollToTop() {
    const el = this.scrollRef.current;
    if (el) el.scrollTop = 0;
    if (this.state.scrollTop !== 0) this.setState({ scrollTop: 0 });
  }

  // Scroll a given row index into view, centering it in the viewport
  // when possible. Used by "Jump to my rank" to land the requesting
  // player roughly in the middle of the visible window rather than at
  // the very top, which feels more like an AAA "find me" jump.
  scrollToIndex(index, { center = true } = {}) {
    const el = this.scrollRef.current;
    if (!el) return;
    const { itemHeight } = this.props;
    if (!itemHeight) return;
    const target = index * itemHeight;
    const offset = center ? Math.max(0, target - el.clientHeight / 2 + itemHeight / 2) : target;
    el.scrollTop = offset;
  }

  componentDidUpdate(_, prevState) {
    const { onVisibleRangeChange, items = [], itemHeight, overscan = 4 } = this.props;
    if (!onVisibleRangeChange || !itemHeight) return;
    const { scrollTop, viewport } = this.state;
    if (
      scrollTop === prevState.scrollTop &&
      viewport === prevState.viewport &&
      items.length === this._lastItemsLen
    ) {
      return;
    }
    this._lastItemsLen = items.length;
    if (viewport <= 0) return;
    const start = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const visibleCount = Math.ceil(viewport / itemHeight);
    const end = Math.min(items.length, start + visibleCount + overscan * 2);
    onVisibleRangeChange(start, end);
  }

  render() {
    const {
      items = [],
      itemHeight,
      overscan = 4,
      renderItem,
      keyFor,
      className = '',
      style,
      header = null,
      footer = null,
    } = this.props;
    const { scrollTop, viewport } = this.state;
    const total = items.length;

    let startIdx = 0;
    let endIdx = total;
    if (viewport > 0 && itemHeight > 0) {
      startIdx = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
      const visibleCount = Math.ceil(viewport / itemHeight);
      endIdx = Math.min(total, startIdx + visibleCount + overscan * 2);
    }

    const totalHeight = total * itemHeight;

    // Build the visible window by index rather than via `.slice().map()`.
    // JS's Array.prototype.map() *skips empty slots in sparse arrays*,
    // which silently drops every placeholder row in callers that pass
    // a sparse list (e.g. the leaderboard's lazy-page model). Iterating
    // by index hands `undefined` to renderItem for unloaded rows so the
    // caller's skeleton path is actually invoked.
    const visible = [];
    for (let index = startIdx; index < endIdx; index++) {
      const item = items[index];
      const k = keyFor ? keyFor(item, index) : (item?.id ?? item?.key ?? index);
      visible.push(
        <div
          key={k}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: itemHeight,
            transform: `translateY(${index * itemHeight}px)`,
          }}
        >
          {renderItem(item, index)}
        </div>
      );
    }

    return (
      <div
        ref={this.scrollRef}
        className={`overflow-y-auto ${className}`}
        style={style}
      >
        {header}
        {/* Spacer wrapper holds the full scrollable height so the
            scrollbar reflects the real list size; visible rows are
            absolute-positioned inside it via translateY so we never
            re-layout off-screen rows. */}
        <div style={{ position: 'relative', height: totalHeight }}>
          {visible}
        </div>
        {footer}
      </div>
    );
  }
}
